import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { T } from '@design/index';
import { GOV_TYPE, govPageContainer } from '../../ui/PatientUIGovernor';
import { patientApi, type HospitalBookingResult, type HospitalPanel as Panel } from '../../api';

/**
 * A hospital's own published panel, and OPD booking into it.
 *
 * The web app only ever knew a hospital as a marketplace card, and "booking
 * OPD" wrote a doctor appointment against a doctor called "OPD Doctor" that
 * no hospital front desk ever saw. `GET /hospitals/:id` is the hospital's own
 * storefront — the departments it runs, the doctors and prices it chose to
 * publish, its licences and whether it takes bookings at all — and
 * `POST /hospitals/:id/appointments` writes the same appointment its front
 * desk creates, for it to confirm at the counter.
 *
 * Nothing here is asserted on the hospital's behalf. A licence number appears
 * only once someone has verified it, a price only if the hospital publishes
 * prices, and a booking is reported as waiting on the hospital — because it
 * is.
 */

const card: React.CSSProperties = {
  background: T.white, border: `1px solid ${T.border}`, borderRadius: 16,
  padding: 16, boxShadow: T.shadow, fontFamily: T.font,
};

const input: React.CSSProperties = {
  width: '100%', padding: '10px 12px', border: `1px solid ${T.border}`,
  borderRadius: 10, fontSize: 13, fontFamily: T.font, color: T.text,
  outline: 'none', boxSizing: 'border-box', background: T.white,
};

const label: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, color: T.textSec, display: 'block', marginBottom: 6,
};

function fmt(v: string): string {
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
}

/** Tomorrow at 10:00, as a value the datetime-local input accepts. */
function defaultWhen(): string {
  const d = new Date(Date.now() + 24 * 60 * 60 * 1000);
  d.setHours(10, 0, 0, 0);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function HospitalPanelPage() {
  const { hospitalId = '' } = useParams();
  const navigate = useNavigate();

  const [panel, setPanel] = useState<Panel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [when, setWhen] = useState(defaultWhen);
  const [departmentId, setDepartmentId] = useState('');
  const [doctorId, setDoctorId] = useState('');
  const [serviceId, setServiceId] = useState('');
  const [reason, setReason] = useState('');
  const [booking, setBooking] = useState(false);
  const [bookError, setBookError] = useState('');
  const [result, setResult] = useState<HospitalBookingResult | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setPanel(await patientApi.hospital.panel(hospitalId));
    } catch (e: any) {
      setError(e?.status === 404
        ? 'This hospital has not opened its panel, so there is nothing published to show.'
        : (e?.body?.message || 'The hospital could not be loaded — check your connection.'));
    } finally {
      setLoading(false);
    }
  }, [hospitalId]);

  useEffect(() => { void load(); }, [load]);

  const chosenService = useMemo(
    () => panel?.services?.find((s) => s.id === serviceId) ?? null,
    [panel, serviceId],
  );

  const book = async () => {
    if (!panel) return;
    setBookError('');
    if (!when) { setBookError('Pick a date and time.'); return; }
    if (new Date(when).getTime() < Date.now()) { setBookError('That time has already passed. Pick a later one.'); return; }
    setBooking(true);
    try {
      setResult(await patientApi.hospital.book(hospitalId, {
        scheduledAt: new Date(when).toISOString(),
        ...(departmentId ? { departmentId } : {}),
        ...(doctorId ? { doctorId } : {}),
        ...(serviceId ? { serviceId } : {}),
        ...(reason.trim() ? { reasonForVisit: reason.trim() } : {}),
      }));
    } catch (e: any) {
      setBookError(e?.body?.errors?.[0]?.message || e?.body?.message || 'The hospital refused that booking.');
    } finally {
      setBooking(false);
    }
  };

  if (loading) {
    return (
      <div style={govPageContainer()}>
        <div style={{ ...card, textAlign: 'center', fontSize: 12, color: T.textSec }}>Loading the hospital…</div>
      </div>
    );
  }

  if (error || !panel) {
    return (
      <div style={govPageContainer()}>
        <div style={{ ...card, textAlign: 'center' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>Not available</div>
          <div style={{ fontSize: 11.5, color: T.textSec, marginTop: 6, lineHeight: 1.6 }}>{error}</div>
          <button
            onClick={() => navigate('/patient/care-services/hospitals')}
            style={{
              marginTop: 12, padding: '9px 16px', borderRadius: 10, border: 'none', cursor: 'pointer',
              fontSize: 11.5, fontWeight: 700, color: T.white, background: T.teal, fontFamily: T.font,
            }}
          >
            Back to hospitals
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...govPageContainer(), display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: 'none', border: 'none', padding: 0, cursor: 'pointer',
            fontSize: 11.5, fontWeight: 700, color: T.teal, fontFamily: T.font, marginBottom: 8,
          }}
        >
          ← Back
        </button>
        <h1 style={{ ...GOV_TYPE.pageTitle }}>
          {panel.displayName}
          {panel.verified && (
            <span style={{
              marginLeft: 8, fontSize: 10, fontWeight: 700, color: T.white,
              background: T.teal, borderRadius: 6, padding: '3px 7px', verticalAlign: 'middle',
            }}>
              NeoVerified
            </span>
          )}
        </h1>
        <p style={{ fontSize: 11.5, color: T.textSec, marginTop: 4, fontFamily: T.font, lineHeight: 1.5 }}>
          {[panel.hospitalType, panel.ownershipType, [panel.addressLine, panel.city, panel.pincode].filter(Boolean).join(', ')]
            .filter(Boolean).join(' · ')}
        </p>
      </div>

      <div style={card}>
        <div style={{ fontSize: 12.5, fontWeight: 800, color: T.text }}>Contact</div>
        <div style={{ fontSize: 11.5, color: T.textSec, marginTop: 6, lineHeight: 1.7 }}>
          {panel.phone && <div>Reception {panel.phone}</div>}
          {panel.emergency && (
            <div>
              {panel.emergency.available ? 'Emergency care on site' : 'No emergency department'}
              {panel.emergency.contact ? ` · ${panel.emergency.contact}` : ''}
            </div>
          )}
          {panel.website && <div>{panel.website}</div>}
          {panel.hospitalNhcId && <div>NeoHealthCard id {panel.hospitalNhcId}</div>}
        </div>
      </div>

      {(panel.facilities?.length ?? 0) > 0 && (
        <div style={card}>
          <div style={{ fontSize: 12.5, fontWeight: 800, color: T.text }}>What it runs</div>
          <div style={{ fontSize: 10.5, color: T.textTer, marginTop: 3 }}>
            Only what a department here actually provides.
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
            {panel.facilities!.map((f) => (
              <span key={f.key} style={{
                fontSize: 10.5, fontWeight: 600, color: T.textSec, background: T.ice,
                border: `1px solid ${T.border}`, borderRadius: 8, padding: '4px 9px',
              }}>
                {f.label}
              </span>
            ))}
          </div>
        </div>
      )}

      {(panel.doctors?.length ?? 0) > 0 && (
        <div style={card}>
          <div style={{ fontSize: 12.5, fontWeight: 800, color: T.text }}>Doctors</div>
          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {panel.doctors!.map((d) => (
              <div key={d.id} style={{ fontSize: 11.5, color: T.textSec }}>
                <span style={{ fontWeight: 700, color: T.text }}>{d.name}</span>
                {d.specialty ? ` · ${d.specialty}` : ''}
              </div>
            ))}
          </div>
        </div>
      )}

      {(panel.services?.length ?? 0) > 0 && (
        <div style={card}>
          <div style={{ fontSize: 12.5, fontWeight: 800, color: T.text }}>Published services</div>
          {!panel.showPrices && (
            <div style={{ fontSize: 10.5, color: T.textTer, marginTop: 3 }}>
              This hospital does not publish its prices; they are quoted at the counter.
            </div>
          )}
          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {panel.services!.map((s) => (
              <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 11.5 }}>
                <span style={{ color: T.text }}>{s.name}</span>
                {typeof s.basePrice === 'number' && (
                  <span style={{ fontWeight: 700, color: T.navy }}>₹{s.basePrice.toLocaleString('en-IN')}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {(panel.licences?.length ?? 0) > 0 && (
        <div style={card}>
          <div style={{ fontSize: 12.5, fontWeight: 800, color: T.text }}>Registration & licences</div>
          <div style={{ fontSize: 11, color: T.textSec, marginTop: 8, lineHeight: 1.7 }}>
            {panel.registrationNumber && <div>Registration {panel.registrationNumber}</div>}
            {panel.accreditation && <div>{panel.accreditation}</div>}
            {panel.licences!.map((l) => (
              <div key={l.licenceId}>
                {l.title}
                {l.number ? ` · ${l.number}` : ''}
                {/* An unchecked number on a public page reads as proof. It is not,
                    so it is withheld and the state is named instead. */}
                <span style={{ color: l.nhcVerified ? T.teal : T.textTer, fontWeight: 600 }}>
                  {l.nhcVerified ? ' · checked by NeoHealthCard' : ' · number not published until checked'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {result ? (
        <div style={{ ...card, borderColor: `${T.teal}66` }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: T.text }}>Requested — waiting on the hospital</div>
          <div style={{ fontSize: 11.5, color: T.textSec, marginTop: 8, lineHeight: 1.7 }}>
            <div>Appointment {result.appointmentNumber}</div>
            <div>{fmt(result.scheduledAt)}</div>
            {result.departmentName && <div>{result.departmentName}</div>}
            {result.doctorName && <div>{result.doctorName}</div>}
            {result.tokenNumber != null && <div>Token {result.tokenNumber}</div>}
            {result.serviceFee > 0 && <div>₹{result.serviceFee.toLocaleString('en-IN')} · {result.paymentStatus.toLowerCase()}</div>}
          </div>
          <div style={{ fontSize: 10.5, color: T.textTer, marginTop: 10, lineHeight: 1.5 }}>
            {panel.displayName} has it in their queue the way they have a walk-in. They confirm it at the counter;
            {result.hospitalPhone ? ` call ${result.hospitalPhone} if you need it sooner.` : ' call the reception if you need it sooner.'}
          </div>
          <button
            onClick={() => navigate('/patient/my-care')}
            style={{
              marginTop: 12, padding: '10px 16px', borderRadius: 10, border: 'none', cursor: 'pointer',
              fontSize: 11.5, fontWeight: 700, color: T.white, background: T.teal, fontFamily: T.font,
            }}
          >
            See it in My Care
          </button>
        </div>
      ) : panel.publicBookingEnabled ? (
        <div style={card}>
          <div style={{ fontSize: 12.5, fontWeight: 800, color: T.text }}>Book OPD</div>
          <div style={{ fontSize: 10.5, color: T.textTer, marginTop: 3, lineHeight: 1.5 }}>
            This goes into the hospital's own appointment book, at the same status a walk-in gets.
          </div>

          <div style={{ marginTop: 12 }}>
            <label htmlFor="hp-when" style={label}>Date and time</label>
            <input id="hp-when" type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} style={input} />
          </div>

          {(panel.departments?.length ?? 0) > 0 && (
            <div style={{ marginTop: 12 }}>
              <label htmlFor="hp-dept" style={label}>Department</label>
              <select id="hp-dept" value={departmentId} onChange={(e) => setDepartmentId(e.target.value)} style={input}>
                <option value="">Let the hospital decide</option>
                {panel.departments!.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
          )}

          {(panel.doctors?.length ?? 0) > 0 && (
            <div style={{ marginTop: 12 }}>
              <label htmlFor="hp-doc" style={label}>Doctor (optional)</label>
              <select id="hp-doc" value={doctorId} onChange={(e) => setDoctorId(e.target.value)} style={input}>
                <option value="">Any available doctor</option>
                {panel.doctors!.map((d) => <option key={d.id} value={d.id}>{d.name}{d.specialty ? ` · ${d.specialty}` : ''}</option>)}
              </select>
            </div>
          )}

          {(panel.services?.length ?? 0) > 0 && (
            <div style={{ marginTop: 12 }}>
              <label htmlFor="hp-svc" style={label}>Service (optional)</label>
              <select id="hp-svc" value={serviceId} onChange={(e) => setServiceId(e.target.value)} style={input}>
                <option value="">Not specified</option>
                {panel.services!.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}{typeof s.basePrice === 'number' ? ` · ₹${s.basePrice.toLocaleString('en-IN')}` : ''}
                  </option>
                ))}
              </select>
              {chosenService && typeof chosenService.basePrice !== 'number' && (
                <div style={{ fontSize: 10.5, color: T.textTer, marginTop: 6 }}>
                  This hospital has not published a price for that; it is charged at the counter.
                </div>
              )}
            </div>
          )}

          <div style={{ marginTop: 12 }}>
            <label htmlFor="hp-reason" style={label}>What is it for? (optional)</label>
            <textarea
              id="hp-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              maxLength={500}
              style={{ ...input, resize: 'vertical' }}
            />
          </div>

          {bookError && <div style={{ fontSize: 11.5, color: T.danger, marginTop: 10 }}>{bookError}</div>}

          <button
            onClick={book}
            disabled={booking}
            style={{
              marginTop: 14, padding: '11px 20px', borderRadius: 10, border: 'none',
              cursor: booking ? 'default' : 'pointer', fontSize: 12.5, fontWeight: 700,
              color: T.white, background: T.teal, fontFamily: T.font, opacity: booking ? 0.6 : 1,
            }}
          >
            {booking ? 'Requesting…' : 'Request this appointment'}
          </button>
        </div>
      ) : (
        <div style={card}>
          <div style={{ fontSize: 12.5, fontWeight: 800, color: T.text }}>Booking is by phone</div>
          <div style={{ fontSize: 11.5, color: T.textSec, marginTop: 6, lineHeight: 1.6 }}>
            {panel.displayName} does not take appointments through the app.
            {panel.phone ? ` Call ${panel.phone} to book.` : ''}
          </div>
        </div>
      )}
    </div>
  );
}
